import { z } from "zod";

const BaseFields = {
    employee_id: z.string().trim().min(1, {
        message: "Employee is required",
    }),

    food_allowance: z
        .number({ message: "Food allowance is required" })
        .min(0, { message: "Food allowance cannot be negative" }),
    food_allowance_by_ar: z.boolean(),

    eid_bonus: z
        .number({ message: "Bonus amount is required" })
        .min(0, { message: "Bonus amount cannot be negative" }),

    food_allowance_note: z.string().trim().max(500, {
        message: "Food allowance note cannot exceed 500 characters",
    }),

    note: z.string().trim().max(1000, {
        message: "Note cannot exceed 1000 characters",
    }),

    type: z.literal("DRAFT"),

    month: z.coerce
        .number({ message: "Month is required" })
        .int({ message: "Month must be a whole number" })
        .min(1, { message: "Month must be between 1 and 12" })
        .max(12, { message: "Month must be between 1 and 12" }),

    year: z.coerce
        .number({ message: "Year is required" })
        .int({ message: "Year must be a whole number" })
        .min(2000, { message: "Please enter a valid year" }),

    status: z.literal("DRAFT"),

    net_amount: z
        .number({ message: "Net amount is required" })
        .min(0, { message: "Net amount cannot be negative" }),
};

export const FreelancerPayslipFormSchema = z.object({
    ...BaseFields,

    total_hours: z.coerce
        .number({ message: "Total hours is required" })
        .min(0, "Total hours must be greater than or equal to 0"),

    hourly_rate: z.coerce
        .number({
            message: "Hourly rate is required",
        })
        .min(0, "Hourly rate must be greater than or equal to 0"),

    deduction: z.coerce
        .number({
            message: "Deduction is required",
        })
        .min(0, "Deduction must be greater than or equal to 0"),

    type: z.literal("HOURLY"),
});

export type FreelancerPayslipFormSchema = z.infer<
    typeof FreelancerPayslipFormSchema
>;

export const ArOfficeStaffPayslipFormSchema = z.object({
    ...BaseFields,

    basic_salary: z
        .number({ message: "Basic salary is required" })
        .min(0, { message: "Basic salary cannot be negative" }),

    commission: z
        .number({ message: "Commission is required" })
        .min(0, { message: "Commission cannot be negative" }),

    absent_deduction: z
        .number({ message: "Absent deduction is required" })
        .min(0, { message: "Absent deduction cannot be negative" }),

    advance_or_other_deduction: z
        .number({ message: "Advance/Other deduction is required" })
        .min(0, { message: "Advance/Other deduction cannot be negative" }),

    balance_to_deduct: z
        .number({ message: "Balance to deduct is required" })
        .min(0, { message: "Balance to deduct cannot be negative" }),

    type: z.literal("MONTHLY_STAFF"),
});

export type ArOfficeStaffPayslipFormSchema = z.infer<
    typeof ArOfficeStaffPayslipFormSchema
>;

export const ArEmployeePayslipFormSchema = z.object({
    ...BaseFields,

    basic_salary: z
        .number({ message: "Basic salary is required" })
        .min(0, { message: "Basic salary cannot be negative" }),

    total_hours: z
        .number({ message: "Total hours is required" })
        .min(0, { message: "Total hours cannot be negative" }),

    total_ot_hours: z
        .number({ message: "Total overtime hours is required" })
        .min(0, { message: "Total overtime hours cannot be negative" }),

    overtime_amount: z
        .number({ message: "Overtime amount is required" })
        .min(0, { message: "Overtime amount cannot be negative" }),

    absent_deduction: z
        .number({ message: "Absent deduction is required" })
        .min(0, { message: "Absent deduction cannot be negative" }),

    advance_or_other_deduction: z
        .number({ message: "Advance/Other deduction is required" })
        .min(0, { message: "Advance/Other deduction cannot be negative" }),

    type: z.literal("MONTHLY_OT"),
});

export type ArEmployeePayslipFormSchema = z.infer<
    typeof ArEmployeePayslipFormSchema
>;
